import { httpsCallable, type FunctionsError } from "firebase/functions";

import type {
  ActivateDeviceRequest,
  ActivateDeviceResponse,
  AdminDanceRetention,
  AdminDanceIdentity,
  AdminDailyFamilyPlays,
  AdminDashboardData,
  AdminFamilyDances,
  ApproveLoginRequestRequest,
  ApproveLoginRequestResponse,
  ApproveLoginRequestWithEmailLinkRequest,
  ApproveLoginRequestWithEmailLinkResponse,
  ApproveLoginRequestWithReviewCodeRequest,
  ApproveLoginRequestWithReviewCodeResponse,
  ClaimAccessResponse,
  CreateBillingCheckoutResponse,
  CreateBillingPortalResponse,
  CreateBrowseArtResponse,
  DeleteFamilyAccountResponse,
  CreateDownloadGrantRequest,
  CreateDownloadGrantResponse,
  CreatePlaybackSessionRequest,
  CreatePlaybackSessionResponse,
  EndPlaybackSessionRequest,
  EndPlaybackSessionResponse,
  GetAdminDanceRetentionRequest,
  GetAdminDailyFamilyPlaysRequest,
  GetAdminFamilyDancesRequest,
  GetBillingCheckoutStatusRequest,
  GetBillingCheckoutStatusResponse,
  GetLoginRequestPublicRequest,
  GetLoginRequestPublicResponse,
  HeartbeatPlaybackSessionRequest,
  HeartbeatPlaybackSessionResponse,
  ListDevicesResponse,
  PlaybackNowPlaying,
  RecordPlaybackChunksRequest,
  RecordPlaybackChunksResponse,
  RefreshPlaybackRequest,
  RefreshPlaybackResponse,
  RemoveDeviceRequest,
  RemoveDeviceResponse,
  RequestSignInLinkRequest,
  RequestSignInLinkResponse,
  RedeemGiftCodeRequest,
  RedeemGiftCodeResponse,
  SignInLinkPlatform,
  SubmitPlayTesterEmailRequest,
  SubmitPlayTesterEmailResponse
} from "@recital-master/shared";

import {
  billingFunctionsEmulatorOrigin,
  edgeFunctions,
  firebaseAuth,
  functions
} from "@/firebase/client";

function callable<Request, Response>(name: string) {
  if (!functions) throw new Error("Firebase Functions are not configured.");
  return httpsCallable<Request, Response>(functions, name);
}

function edgeCallable<Request, Response>(name: string) {
  if (!edgeFunctions) throw new Error("Firebase Functions are not configured.");
  return httpsCallable<Request, Response>(edgeFunctions, name);
}

async function callBilling<Request, Response>(
  name: string,
  data: Request
): Promise<Response> {
  if (!billingFunctionsEmulatorOrigin) {
    const response = await callable<Request, Response>(name)(data);
    return response.data;
  }

  const token = await firebaseAuth?.currentUser?.getIdToken();
  if (!token) throw Object.assign(new Error("Sign in to continue."), { code: "functions/unauthenticated" });
  const response = await fetch(`${billingFunctionsEmulatorOrigin}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ data: data ?? null })
  });
  const payload = (await response.json()) as {
    result?: Response;
    data?: Response;
    error?: { status?: string; message?: string };
  };
  if (!response.ok || payload.error) {
    const status = payload.error?.status?.toLowerCase().replaceAll("_", "-") ?? "unknown";
    throw Object.assign(
      new Error(payload.error?.message || "Billing is unavailable in local development."),
      { code: `functions/${status}` }
    );
  }
  return (payload.result ?? payload.data) as Response;
}

export function firebaseErrorCode(error: unknown): string | null {
  return typeof error === "object" && error != null && "code" in error
    ? String((error as FunctionsError).code).replace(/^functions\//, "")
    : null;
}

export function firebaseErrorMessage(error: unknown): string {
  const code = firebaseErrorCode(error);
  if (code == null || code === "internal" || code === "unknown") {
    return "Something went wrong on our end. Try again in a moment.";
  }
  if (typeof error === "object" && error != null && "message" in error) {
    const message = String((error as Error).message).trim();
    if (message && message !== code && message !== `functions/${code}`) return message;
  }
  return "Something went wrong on our end. Try again in a moment.";
}

export async function claimAccess(): Promise<ClaimAccessResponse> {
  const response = await callable<void, ClaimAccessResponse>("claimAccess")();
  return response.data;
}

export async function submitPlayTesterEmail(
  request: SubmitPlayTesterEmailRequest
): Promise<SubmitPlayTesterEmailResponse> {
  const response = await callable<
    SubmitPlayTesterEmailRequest,
    SubmitPlayTesterEmailResponse
  >("submitPlayTesterEmail")(request);
  return response.data;
}

export async function redeemGiftCode(
  request: RedeemGiftCodeRequest
): Promise<RedeemGiftCodeResponse> {
  const response = await callable<RedeemGiftCodeRequest, RedeemGiftCodeResponse>(
    "redeemGiftCode"
  )(request);
  return response.data;
}

export async function requestSignInLink(
  email: string,
  platform: SignInLinkPlatform,
  request: Omit<RequestSignInLinkRequest, "email" | "platform">
): Promise<RequestSignInLinkResponse> {
  const response = await edgeCallable<RequestSignInLinkRequest, RequestSignInLinkResponse>(
    "requestSignInLink"
  )({ email, platform, ...request });
  return response.data;
}

export async function getLoginRequestPublic(requestId: string) {
  const response = await callable<GetLoginRequestPublicRequest, GetLoginRequestPublicResponse>(
    "getLoginRequestPublic"
  )({ requestId });
  return response.data;
}

export async function approveLoginRequest(requestId: string) {
  const response = await callable<ApproveLoginRequestRequest, ApproveLoginRequestResponse>(
    "approveLoginRequest"
  )({ requestId });
  return response.data;
}

export async function approveLoginRequestWithEmailLink(requestId: string, oobCode: string) {
  const response = await callable<
    ApproveLoginRequestWithEmailLinkRequest,
    ApproveLoginRequestWithEmailLinkResponse
  >("approveLoginRequestWithEmailLink")({ requestId, oobCode });
  return response.data;
}

export async function approveLoginRequestWithReviewCode(requestId: string, code: string) {
  const response = await callable<
    ApproveLoginRequestWithReviewCodeRequest,
    ApproveLoginRequestWithReviewCodeResponse
  >("approveLoginRequestWithReviewCode")({ requestId, code });
  return response.data;
}

export async function activateDevice(
  request: ActivateDeviceRequest
): Promise<ActivateDeviceResponse> {
  const response = await edgeCallable<ActivateDeviceRequest, ActivateDeviceResponse>(
    "activateDevice"
  )(request);
  return response.data;
}

export async function removeDevice(deviceId: string): Promise<RemoveDeviceResponse> {
  const response = await callable<RemoveDeviceRequest, RemoveDeviceResponse>("removeDevice")({
    deviceId
  });
  return response.data;
}

export async function listDevices(): Promise<ListDevicesResponse> {
  const response = await callable<void, ListDevicesResponse>("listDevices")();
  return response.data;
}

export async function deleteFamilyAccount(): Promise<DeleteFamilyAccountResponse> {
  const response = await callable<void, DeleteFamilyAccountResponse>(
    "deleteFamilyAccount"
  )();
  return response.data;
}

export async function createBillingCheckout(): Promise<CreateBillingCheckoutResponse> {
  return callBilling<void, CreateBillingCheckoutResponse>("createBillingCheckout", undefined);
}

export async function createBillingPortal(): Promise<CreateBillingPortalResponse> {
  return callBilling<void, CreateBillingPortalResponse>("createBillingPortal", undefined);
}

export async function getBillingCheckoutStatus(
  sessionId: string
): Promise<GetBillingCheckoutStatusResponse> {
  return callBilling<
    GetBillingCheckoutStatusRequest,
    GetBillingCheckoutStatusResponse
  >("getBillingCheckoutStatus", { sessionId });
}

export async function createBrowseArt(): Promise<CreateBrowseArtResponse> {
  const response = await callable<void, CreateBrowseArtResponse>("createBrowseArt")();
  return response.data;
}

export async function createDownloadGrant(
  request: CreateDownloadGrantRequest
): Promise<CreateDownloadGrantResponse> {
  const response = await callable<CreateDownloadGrantRequest, CreateDownloadGrantResponse>(
    "createDownloadGrant"
  )(request);
  return response.data;
}

export async function createPlaybackSession(
  request: CreatePlaybackSessionRequest
): Promise<CreatePlaybackSessionResponse> {
  const response = await callable<CreatePlaybackSessionRequest, CreatePlaybackSessionResponse>(
    "createPlaybackSession"
  )(request);
  return response.data;
}

export async function refreshPlayback(
  request: RefreshPlaybackRequest
): Promise<RefreshPlaybackResponse> {
  const response = await callable<RefreshPlaybackRequest, RefreshPlaybackResponse>(
    "refreshPlayback"
  )(request);
  return response.data;
}

export async function heartbeatPlaybackSession(
  sessionId: string,
  nowPlaying?: PlaybackNowPlaying,
  playbackActive?: boolean
): Promise<HeartbeatPlaybackSessionResponse> {
  const response = await callable<
    HeartbeatPlaybackSessionRequest,
    HeartbeatPlaybackSessionResponse
  >("heartbeatPlaybackSession")({
    sessionId,
    ...(nowPlaying ? { nowPlaying } : {}),
    ...(playbackActive !== undefined ? { playbackActive } : {})
  });
  return response.data;
}

export async function endPlaybackSession(sessionId: string): Promise<EndPlaybackSessionResponse> {
  const response = await callable<EndPlaybackSessionRequest, EndPlaybackSessionResponse>(
    "endPlaybackSession"
  )({ sessionId });
  return response.data;
}

export async function recordPlaybackChunks(
  request: RecordPlaybackChunksRequest
): Promise<RecordPlaybackChunksResponse> {
  const response = await callable<RecordPlaybackChunksRequest, RecordPlaybackChunksResponse>(
    "recordPlaybackChunks"
  )(request);
  return response.data;
}

export async function getAdminDashboard(): Promise<AdminDashboardData> {
  const response = await callable<void, AdminDashboardData>("getAdminDashboard")();
  return response.data;
}

export async function getAdminDailyFamilyPlays(
  familyAccountId: string,
  date: string
): Promise<AdminDailyFamilyPlays> {
  const response = await callable<
    GetAdminDailyFamilyPlaysRequest,
    AdminDailyFamilyPlays
  >("getAdminDailyFamilyPlays")({familyAccountId, date});
  return response.data;
}

export async function getAdminDanceRetention(
  dance: AdminDanceIdentity,
  familyAccountId?: string
): Promise<AdminDanceRetention> {
  const response = await callable<GetAdminDanceRetentionRequest, AdminDanceRetention>(
    "getAdminDanceRetention"
  )({ ...dance, ...(familyAccountId ? { familyAccountId } : {}) });
  return response.data;
}

export async function getAdminFamilyDances(
  familyAccountId: string
): Promise<AdminFamilyDances> {
  const response = await callable<GetAdminFamilyDancesRequest, AdminFamilyDances>(
    "getAdminFamilyDances"
  )({ familyAccountId });
  return response.data;
}
